import { FC } from 'react'
import { Route, Routes, Navigate, useLocation } from 'react-router-dom'
import { MasterLayout } from '../../_payairo/layout/MasterLayout'
import { DashboardWrapper } from '../pages/dashboard/DashboardWrapper'
import { lazyRoute } from './lazyRoute'
import { UserManagementWrapper } from '../pages/userManagement/UserManagement'

import { RolesManagementWrapper } from '../pages/rolesManagement/RolesManagement'
import { useAuth } from '../modules/auth'
import { getDashboardPath } from '../utils/helper'
import { ProtectedRoute } from '../App'
import ManagedModuleAssets from '../pages/managedModuleAssets/ManagedModuleAssets'
import { ENABLE_NOTIFICATIONS } from '../hooks/useNotifications'

// Route guard component to redirect /dashboard to the actual super-admin dashboard
const DashboardRouteGuard: FC<{ children: React.ReactElement }> = ({ children }) => {
  const location = useLocation()
  const path = location.pathname

  if (
    path === '/dashboard' ||
    path === '/customer-support/dashboard' ||
    path === '/spectator/dashboard' ||
    path === '/manager/dashboard'
  ) {
    return <Navigate to='/super-admin/dashboard' replace />
  }

  return children
}

// Admin-only guards (now just pass-throughs as only Admin flow remains)
const AdminOnlyRouteGuard: FC<{ children: React.ReactElement }> = ({ children }) => children
const RoleManagementRouteGuard: FC<{ children: React.ReactElement }> = ({ children }) => children
const AdminAndSpectatorRouteGuard: FC<{ children: React.ReactElement }> = ({ children }) => children

/** Module-level lazy routes: stable component identity + chunk retry (see `lazyRoute.ts`). */
const AnnouncementManagement = lazyRoute(() => import('../pages/announcement/AnnouncementManagement'))
const PayAiroUserManagement = lazyRoute(() => import('../pages/payairouser/PayAiroUserManagement'))
const SetLimitsWrapper = lazyRoute(() => import('../pages/setLimits/SetLimits'))
const FeeSettingsWrapper = lazyRoute(() => import('../pages/feeSettings/FeeSettings'))
const SupportCenterWrapper = lazyRoute(() => import('../pages/supportCenter/Support'))
const TransactionHistoryWrapper = lazyRoute(() => import('../pages/transactions/AllTransactions'))
const UserDetailsPage = lazyRoute(() => import('../pages/userDetails/UserDetailsPage'))
const UserTransactionsPage = lazyRoute(() => import('../pages/payairouser/UserTransactionsPage'))
const MyProfileWrapper = lazyRoute(() => import('../pages/myProfile/MyProfile'))
const CryptoTransactionHistoryWrapper = lazyRoute(() => import('../pages/transactions/CryptoTransactions'))
const NotificationsPage = lazyRoute(() => import('../pages/notifications/NotificationsPage'))
const SupportPerformanceWrapper = lazyRoute(() => import('../pages/supportPerformance/SupportPerformance'))
const SmsProviders = lazyRoute(() => import('../pages/Settings/SmsProviders'))
const SDUILayoutEditor = lazyRoute(() => import('../pages/sduiLayoutEditor/SDUILayoutEditor'))
const SduiAssetManager = lazyRoute(() => import('../pages/sduiAssetManager/SduiAssetManager'))
const BlogManagementWrapper = lazyRoute(() => import('../pages/blogManagement/BlogManagementWrapper'))
const BlogList = lazyRoute(() => import('../pages/blogManagement/BlogList'))
const BlogEdit = lazyRoute(() => import('../pages/blogManagement/BlogEdit'))
const RewardsConfigurationPage = lazyRoute(() => import('../modules/rewards/pages/RewardsConfigurationPage'))
const PartnersManagementPage = lazyRoute(() => import('../modules/rewards/pages/PartnersManagementPage'))


const PrivateRoutes = () => {
  const { currentUser, auth } = useAuth()
  const role = currentUser?.role || auth?.role || currentUser?.user_type
  const dashboardPath = getDashboardPath(role, currentUser?.user_type)

  return (
    <>
      <Routes>
        <Route element={<MasterLayout />}>
          {/* Redirect to Dashboard after success login/registartion */}
          <Route path='auth/*' element={<Navigate to={dashboardPath} replace />} />
          {/* Pages */}
          {/* Super Admin Dashboard */}
          <Route
            path='dashboard'
            element={
              <DashboardRouteGuard>
                <DashboardWrapper />
              </DashboardRouteGuard>
            }
          />
          <Route
            path='super-admin/dashboard'
            element={
              <DashboardRouteGuard>
                <DashboardWrapper />
              </DashboardRouteGuard>
            }
          />
          <Route
            path='all-users'
            element={
              <AdminOnlyRouteGuard>
                <UserManagementWrapper />
              </AdminOnlyRouteGuard>
            }
          />
          <Route
            path='access-management'
            element={
              <AdminOnlyRouteGuard>
                <UserManagementWrapper />
              </AdminOnlyRouteGuard>
            }
          />

          <Route
            path='role-management'
            element={
              <RoleManagementRouteGuard>
                <RolesManagementWrapper />
              </RoleManagementRouteGuard>
            }
          />

          <Route
            path='transactions'
            element={<Navigate to='/transactions/fiat' replace />}
          />
          <Route
            path='transactions/fiat'
            element={
              <AdminAndSpectatorRouteGuard>
                <TransactionHistoryWrapper />
              </AdminAndSpectatorRouteGuard>
            }
          />
          <Route
            path='transactions/crypto'
            element={
              <AdminAndSpectatorRouteGuard>
                <CryptoTransactionHistoryWrapper />
              </AdminAndSpectatorRouteGuard>
            }
          />
          <Route
            path='settings'
            element={
              <AdminAndSpectatorRouteGuard>
                <MyProfileWrapper />
              </AdminAndSpectatorRouteGuard>
            }
          />
          <Route
            path='sms-providers'
            element={
              <AdminOnlyRouteGuard>
                <SmsProviders />
              </AdminOnlyRouteGuard>
            }
          />
          <Route
            path='managed-module-assets'
            element={
              <AdminAndSpectatorRouteGuard>
                <ManagedModuleAssets />
              </AdminAndSpectatorRouteGuard>
            }
          />
          <Route
            path='sdui-layout-editor'
            element={
              <AdminOnlyRouteGuard>
                <SDUILayoutEditor />
              </AdminOnlyRouteGuard>
            }
          />
          <Route
            path='sdui-asset-manager'
            element={
              <AdminOnlyRouteGuard>
                <SduiAssetManager />
              </AdminOnlyRouteGuard>
            }
          />
          <Route
            path='rewards/configuration'
            element={
              <AdminOnlyRouteGuard>
                <RewardsConfigurationPage />
              </AdminOnlyRouteGuard>
            }
          />
          <Route
            path='rewards/partners'
            element={
              <AdminOnlyRouteGuard>
                <PartnersManagementPage />
              </AdminOnlyRouteGuard>
            }
          />
          <Route
            path='blog-access'
            element={
              <AdminAndSpectatorRouteGuard>
                <BlogManagementWrapper />
              </AdminAndSpectatorRouteGuard>
            }
          >
            <Route index element={<BlogList />} />
            <Route path='new' element={<BlogEdit />} />
            <Route path='edit/:id' element={<BlogEdit />} />
          </Route>
          <Route
            path='admin/support-performance'
            element={
              <AdminOnlyRouteGuard>
                <SupportPerformanceWrapper />
              </AdminOnlyRouteGuard>
            }
          />
          <Route
            path='manager/support-performance'
            element={
              <ProtectedRoute allowedRoles={['Manager']}>
                <DashboardRouteGuard>
                  <SupportPerformanceWrapper />
                </DashboardRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path='announcements'
            element={
              <AdminAndSpectatorRouteGuard>
                <AnnouncementManagement />
              </AdminAndSpectatorRouteGuard>
            }
          />
          <Route
            path='payairo-users'
            element={
              <AdminAndSpectatorRouteGuard>
                <PayAiroUserManagement />
              </AdminAndSpectatorRouteGuard>
            }
          />
          <Route
            path='set-limits'
            element={
              <AdminOnlyRouteGuard>
                <SetLimitsWrapper />
              </AdminOnlyRouteGuard>
            }
          />
          <Route
            path='fee-settings'
            element={
              <AdminOnlyRouteGuard>
                <FeeSettingsWrapper />
              </AdminOnlyRouteGuard>
            }
          />
          <Route
            path='support'
            element={
              <AdminAndSpectatorRouteGuard>
                <SupportCenterWrapper />
              </AdminAndSpectatorRouteGuard>
            }
          />
          {ENABLE_NOTIFICATIONS && (
            <Route
              path='notifications'
              element={
                <AdminAndSpectatorRouteGuard>
                  <NotificationsPage />
                </AdminAndSpectatorRouteGuard>
              }
            />
          )}
          <Route
            path='user/:id'
            element={
              <AdminAndSpectatorRouteGuard>
                <UserDetailsPage />
              </AdminAndSpectatorRouteGuard>
            }
          />
          <Route
            path='user/:id/transactions'
            element={
              <AdminAndSpectatorRouteGuard>
                <UserTransactionsPage />
              </AdminAndSpectatorRouteGuard>
            }
          />
          <Route
            path='user-transactions/:id'
            element={
              <AdminAndSpectatorRouteGuard>
                <UserTransactionsPage />
              </AdminAndSpectatorRouteGuard>
            }
          />

          {/* <Route path='urlregistration' element={<UrlRegistration />} />
        <Route path='urlmapping' element={<UrlMapping />} />
        <Route path='myip' element={<MyIP />} />
        <Route path='vendorlist' element={<VendorListWrapper />} />
        <Route path='manageroutes' element={<ManageRoutes />} />
        <Route path='manageroutetree' element={<ManageRoutesTree />} /> */}
          {/* <Route path='ucccomplaints' element={<UccComplaintsWrapper />} /> */}
          {/* <Route path='smspack' element={<SMSPackWrapper />} /> */}
          {/* <Route path='orderslist' element={<OrdersListWrapper />} />
        <Route path='invoice' element={<InvoiceWrapper />} />
        <Route path='bulk-report' element={<BulkReport />} /> */}

          {/* Lazy Modules */}
          {/* <Route
          path='crafted/pages/profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/pages/wizards/*'
          element={
            <SuspensedView>
              <WizardsPage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/widgets/*'
          element={
            <SuspensedView>
              <WidgetsPage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/account/*'
          element={
            <SuspensedView>
              <AccountPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/chat/*'
          element={
            <SuspensedView>
              <ChatPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/user-management/*'
          element={
            <SuspensedView>
              <UsersPage />
            </SuspensedView>
          }
        /> */}
          {/* Page Not Found */}
          <Route path='*' element={<Navigate to='/error/404' />} />
        </Route>

        {/* <Route element={<PaymentLayout />}>
        <Route path='payment' element={<Payment />} />
      </Route> */}
      </Routes>
    </>
  )
}

export { PrivateRoutes }
